import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  DialogContentText,
  FormControlLabel,
  Switch,
  Box
} from '@mui/material';
import { useSearchContext } from '../../contexts/SearchContext';

interface SaveSearchDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Dialog for entering a name when saving a search
 * Uses the enhanced SearchContext for saving searches
 */
const SaveSearchDialog: React.FC<SaveSearchDialogProps> = ({ open, onClose }) => {
  // Get saveCurrentSearch function and state from SearchContext
  const { saveCurrentSearch, isLoading, error: contextError } = useSearchContext();
  
  const [searchName, setSearchName] = useState('');
  const [autoSave, setAutoSave] = useState(true);
  const [localError, setLocalError] = useState('');
  
  // Use the error from context if available, otherwise use local error
  const error = contextError || localError;
  
  // Reset local state when dialog opens
  useEffect(() => {
    if (open) {
      setSearchName('');
      setAutoSave(true);
      setLocalError('');
    }
  }, [open]);

  const handleSave = async () => {
    if (!searchName.trim()) {
      setLocalError('Please enter a name for this search');
      return;
    }
    
    setLocalError(''); // Clear previous local error
    
    try {
      const success = await saveCurrentSearch(searchName, autoSave);
      if (success) {
        handleClose();
      }
      // We don't need to set errors here as the context will handle that
    } catch (err) {
      console.error("Error during save:", err);
      setLocalError('An unexpected error occurred during save.');
    }
  };

  const handleClose = () => {
    setSearchName('');
    setAutoSave(true);
    setLocalError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save Search</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Enter a name for this search to save the current location, month, and service definitions.
        </DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          id="name"
          label="Search Name"
          type="text"
          fullWidth
          variant="outlined"
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          error={!!error}
          helperText={error}
          sx={{ mt: 2 }}
        />
        
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoSave}
                onChange={(e) => setAutoSave(e.target.checked)}
                color="primary"
              />
            }
            label="Auto-save when parameters change"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveSearchDialog;
